import React, { useState } from 'react'
import { api, formatCurrency } from '../../utils/api'

/**
 * Day 14, no reply (spec 20261103): confirm List it or Bin it for one item.
 *
 * List it pays the revised offer and sends the item on to photography and
 * listing, which is what Accept does. The worker moves the item and hands back
 * the item in the shape the photography queue takes, so it is queued here with
 * the same call Accept uses. Bin it moves a £0 item to disposed.
 *
 * `item` is { quoteItemId, name, revisedOffer }. Calls onDone(warnings) once
 * the item has moved. A 409 is shown as Prism's own reason.
 */
function DecisionDialog({ dealId, item, action, onDone, onClose }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const isList = action === 'list'

  async function handleConfirm() {
    setSaving(true)
    setError(null)
    let response
    try {
      response = await api.post(`/api/warehouse/decisions/${action}`, {
        dealId,
        quoteItemId: item.quoteItemId
      })
    } catch (err) {
      setError(err.message || 'Could not update the item. Try again.')
      setSaving(false)
      return
    }
    onDone(await queueForPhotography(response))
  }

  // The item has moved by now, so a failure here is a warning, not an error.
  async function queueForPhotography(response) {
    const warnings = [...(response?.warnings || [])]
    if (!isList || !response?.queueItem) return warnings
    try {
      await api.post('/api/warehouse/issue-trades/queue-photography', {
        dealId,
        item: response.queueItem
      })
    } catch (err) {
      warnings.push(
        `${item.name} is accepted for payment, but it was not queued for photography: ` +
        `${err.message || 'unknown error'}. Queue it from the deal.`
      )
    }
    return warnings
  }

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="decision-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: '100%' }}
      >
        <div className="modal-header">
          <h3 id="decision-title" style={{ margin: 0, fontSize: 18 }}>{isList ? 'List it' : 'Bin it'}</h3>
          <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 15 }}>
            {isList ? (
              <>
                <strong>{item.name}</strong>: the seller did not reply, so the revised offer
                of {formatCurrency(item.revisedOffer || 0)} counts as accepted. It goes to
                Awaiting Payment and on to photography.
              </>
            ) : (
              <>
                <strong>{item.name}</strong>: the seller did not reply and the revised offer is £0.
                The item is binned. This cannot be undone.
              </>
            )}
          </div>
          {error && <div role="alert" style={{ color: '#E5625E', fontSize: 14 }}>{error}</div>}
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: 12, padding: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving} style={{ minHeight: 44, flex: 1 }}>
            Cancel
          </button>
          <button
            className={`btn ${isList ? 'btn-success' : 'btn-danger'}`}
            onClick={handleConfirm}
            disabled={saving}
            style={{ minHeight: 44, flex: 1 }}
          >
            {saving ? 'Saving...' : (isList ? 'List it' : 'Bin it')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DecisionDialog
